import * as React from 'react';

import { IParameterFieldBaseOptions } from '../ts/ParameterFieldBase';

export interface INumberFieldOptions extends IParameterFieldBaseOptions {
    allowZeroInsteadOfNull?: boolean;
    integer?: boolean;
    inline?: boolean; //LL: set in Charts limit, but not applied. Seems functionality is lost somewhere. Oh, remains in StringField.tsx
    min?: number;
    max?: number;
    applyOnEnter?: boolean;
    applyOnBlur?: boolean;
}

export interface INumberFieldProperties<TOptions extends INumberFieldOptions = INumberFieldOptions> {
    value?: number | string | any[];

    allowNull?: boolean;
    allowZeroInsteadOfNull?: boolean; //LL: 15.10.21
    integer?: boolean; // default true
    placeholder?: string;
    readOnly?: boolean;
    setFocus?: boolean;
    options?: TOptions;
    positive?: boolean;
    notNegative?: boolean;
    min?: number;
    max?: number;
    minConstraintAllowEqual?: boolean; //LL: 18.12.18  default true
    maxConstraintAllowEqual?: boolean; //LL: 18.12.18  default true
    emptyValue?: number;
    tooltip?: string;

    //LL: BrokenMinMaxConstraints:
    showBrokenMinMaxConstraints?: boolean;
    restoreOnApplyIfBrokenMinMaxConstraints?: boolean; //LL: 16.01.19 has sense when showBrokenMinMaxConstraints is true
    emitErrorWhenBrokenMinMaxConstraints?: boolean;
    onErrorBrokenMinMaxConstraints?(value: number): void;

    applyOnEnter?: boolean;
    applyOnBlur?: boolean;

    //EVG:
    // It is wrong that this onChange does not fire on value's change.
    // onChange must work as everywhere by default.
    // If another operation is needed, it should be changed by flags.
    // However, apply on blur is considered default in many places. So, I keep it to be default.
    // To make onChange fire on each change, flags must be: applyOnEnter !== true and applyOnBlur === false.
    onChange?(value: number): void;
    onBlur?(value: number): void;
    onEnterPress?(value: number): void;
    //onFocus?(value: number): void;
}

function minConstraintBroken(value, min: number, allowEqual: boolean): boolean {
    if (min == null) {
        return false;
    }

    if (allowEqual) {
        return value < min;
    } else {
        return value <= min;
    }
}

function maxConstraintBroken(value, max: number, allowEqual: boolean): boolean {
    if (max == null) {
        return false;
    }

    if (allowEqual) {
        return value > max;
    } else {
        return value >= max;
    }
}

function getMin(props: INumberFieldProperties) {
    let minValue: number;

    if (props.min != null) {
        minValue = props.min;
    } else if (props.options != null && props.options.min != null) {
        minValue = props.options.min;
    }

    return minValue;
}

function getMax(props: INumberFieldProperties) {
    let maxValue: number;

    if (props.max != null) {
        maxValue = props.max;
    } else if (props.options != null && props.options.max != null) {
        maxValue = props.options.max;
    }

    return maxValue;
}

function getMinConstraintAllowEqual(props: INumberFieldProperties) {
    return props.minConstraintAllowEqual ?? true;
}

function getMaxConstraintAllowEqual(props: INumberFieldProperties) {
    return props.maxConstraintAllowEqual ?? true;
}

function getAllowNull(props: INumberFieldProperties) {
    return props.allowNull ?? props.options?.allowNull;
}

function getEmptyValue(props: INumberFieldProperties) {
    let emptyVal: number = 0;

    if (props.positive) {
        emptyVal = 1;
    }

    if (props.emptyValue != null) {
        emptyVal = props.emptyValue;
    }

    const min = getMin(props);
    const max = getMax(props);

    if (minConstraintBroken(emptyVal, min, getMinConstraintAllowEqual(props))) {
        emptyVal = min;
    } else if (maxConstraintBroken(emptyVal, max, getMaxConstraintAllowEqual(props))) {
        emptyVal = max;
    }

    return emptyVal;
}

function isInteger(props: INumberFieldProperties) {
    if (props.integer != null) {
        return props.integer;
    } else if (props.options != null && props.options.integer != null) {
        return props.options.integer;
    }

    return true;
}

function getValue(value: number | string | any[], isInteger: boolean): number {
    let result: any;

    if (Array.isArray(value)) {
        result = value.length > 0 ? value[0] : null;
    } else {
        result = value;
    }

    if (typeof result === 'string') {
        if (isInteger) {
            result = parseInt(result, 10);
        } else {
            result = parseFloat(result);
        }
    }

    return result;
}

export function getStringValue(props: INumberFieldProperties, value?: number | string | any[]): string {
    value = getValue(value === undefined ? props.value : value, isInteger(props));

    if (value == null) {
        const emptyValue = getEmptyValue(props).toString();

        return getAllowNull(props) !== true ? emptyValue : '';
    } else {
        return value.toString();
    }
}

export function parseValue(value: string, props: INumberFieldProperties): number {
    if (isInteger(props)) {
        //LL: 26.12.18  parseInt("1a3") returns 1  !!!
        //   see https://javascript.ru/parseint ,  https://javascript.ru/parseFloat :
        //   Для более строгого преобразовния строки в число используйте оператор "+".
        //   Он возвращает NaN, если строка в точности не является числом.
        //    Чтобы преобразовать строку, в точности являющуюся числом, используйте оператор "+":
        //       let str = "123.6"
        //       +str // 123.6 - не округляет
        //    При любых ошибках преобразования оператор "+" возвращает NaN.
        //       let str = "123bc"
        //       +str // NaN
        //       parseInt(str,10) // 123

        //LL: also  num = Number(val)  may be used

        let n = +value;

        if (isNaN(n)) {
            return n;
        } else {
            return parseInt(value);
        }
    } else {
        let v = value.replace(',', '.');
        let n = +v;

        if (isNaN(n)) {
            return n;
        } else {
            return parseFloat(v);
        }
    }
}

function checkConstraints(
    value: string,
    props: INumberFieldProperties
): {
    broken: boolean;
    brokenOnlyMinMax: boolean;
} {
    let broken = false;
    let brokenOnlyMinMax = false;

    if (value !== '' && value !== '-') {
        const parsed = parseValue(value, props);

        if (isNaN(parsed)) {
            broken = true;
        } else {
            if (isInteger(props)) {
                //LL: 29.07.20: it was wrong when leading zeros: 4.toString() !== "04"  !!!
                //  str.padStart(targetLength [, padString])
                //     Метод padStart() заполняет текущую строку другой строкой (несколько раз, если нужно) так,
                //     что итоговая строка достигает заданной длины.
                //     Заполнение осуществляется в начале (слева) текущей строки.
                //    'abc'.padStart(10, "foo");  // "foofoofabc"
                //    'abc'.padStart(6,"123465"); // "123abc"
                //    'abc'.padStart(8, "0");     // "00000abc"

                //LL: 12.08.20: wrong for negative values: value = "-0179", parsed = -179: "-0179" ~~ "0-179"
                let isNeg = parsed < 0;
                let val = value;
                let parsedPositiv = parsed;

                if (isNeg) {
                    val = val.substr(1);
                    parsedPositiv = -parsed;
                }

                let N = val.length;
                let cmpStr = parsedPositiv.toString().padStart(N, '0');

                //  without leading '-':
                if (cmpStr !== val) {
                    broken = true;
                }
            }

            if (!broken && props.notNegative) {
                if (parsed < 0) {
                    broken = true;
                }
            }

            if (!broken && props.positive) {
                if (parsed <= 0) {
                    broken = true;
                }
            }

            //LL: 18.12.18 min max - the last
            if (
                !broken &&
                (minConstraintBroken(parsed, getMin(props), getMinConstraintAllowEqual(props)) ||
                    maxConstraintBroken(parsed, getMax(props), getMaxConstraintAllowEqual(props)))
            ) {
                broken = true;
                brokenOnlyMinMax = true;
            }
        }
    }

    return {
        broken: broken,
        brokenOnlyMinMax: brokenOnlyMinMax
    };
}

export interface INumberFieldStatelessProperties extends INumberFieldProperties {
    inputControlRef?: React.RefObject<HTMLInputElement>;
    isNotFormControl?: boolean;
    onSetState?(value: string): void;
}

export function NumberFieldStateless(props: INumberFieldStatelessProperties) {
    const [, setUpdateFlag] = React.useState(0);

    const { options } = props;
    const readOnly = props.readOnly ?? options?.readOnly;
    const applyOnEnter = props.applyOnEnter ?? options?.applyOnEnter;
    const value = props.value as string;

    const processOnChange = (showActualValue = true, currentValue = value) => {
        if (readOnly) {
            return;
        }

        let result = parseValue(currentValue, props);

        if (isNaN(result)) {
            const emptyValue = getEmptyValue(props);

            result = getAllowNull(props) !== true ? emptyValue : null;
        }

        let emit = true;
        let broken = false;
        let brokenOnlyMinMax = false;

        if (result != null) {
            let brokenObj = checkConstraints(result.toString(), props);

            broken = brokenObj.broken;
            brokenOnlyMinMax = brokenObj.brokenOnlyMinMax;

            if (broken) {
                emit = false;
            }
        }

        if (brokenOnlyMinMax && props.emitErrorWhenBrokenMinMaxConstraints) {
            props.onErrorBrokenMinMaxConstraints?.(result);
        }

        if (emit && props.onChange != null) {
            props.onChange(result);
        }

        if (broken && props.restoreOnApplyIfBrokenMinMaxConstraints) {
            result = getValue(props.value, isInteger(props));
        }

        if (showActualValue) {
            const resultValue = getStringValue(props, result);

            if (resultValue !== currentValue) {
                props.onSetState?.(resultValue);
            }
        }

        return result;
    };

    const handleOnChange = (e) => {
        let newValue = props.inputControlRef.current.value;

        if (newValue === '' || newValue === '-') {
            // Allowed without rising events.
            props.onSetState?.(newValue);
            return;
        }

        const parsed = parseValue(newValue, props);

        if (isNaN(parsed)) {
            // Drop the change.
            setUpdateFlag((updateFlag) => ++updateFlag);
            return;
        }

        if (
            !isInteger(props) &&
            (newValue.indexOf('.') === newValue.length - 1 || newValue.indexOf(',') === newValue.length - 1)
        ) {
            // For float number the only dot or comma is allowed without rising events.
            props.onSetState?.(newValue);
            return;
        }

        //LL: 30.05.19: more strict:
        //   if brokenAll except MinMax => restore value from state
        //   if brokenOnlyMinMax => depends on showBrokenMinMaxConstraints
        const brokenObj = checkConstraints(newValue, props);

        if (brokenObj.brokenOnlyMinMax) {
            if (props.showBrokenMinMaxConstraints !== true) {
                newValue = value;
            }
        } else if (brokenObj.broken) {
            newValue = value;
        }

        if (newValue !== value) {
            props.onSetState?.(newValue);

            if (!applyOnEnter && (props.applyOnBlur ?? options?.applyOnBlur) === false) {
                processOnChange(false, newValue);
            }
        }
    };

    const handleKeyUp = (e) => {
        if (e.code === 'Enter' || e.code === 'NumpadEnter') {
            if (applyOnEnter) {
                processOnChange();
            }

            if (props.onEnterPress != null) {
                const newValue = processOnChange();

                props.onEnterPress(newValue);
            }
        }
    };

    const handleOnBlur = (e) => {
        const newValue = processOnChange();

        if (props.onBlur != null) {
            props.onBlur(newValue);
        }
    };

    // const handleOnFocus = (e) => {
    //     if (typeof props.onFocus === 'function') {
    //         const newValue = processOnChange();

    //         props.onFocus(newValue);
    //     }
    // };

    let content: React.JSX.Element;
    let zeroValue: number;

    if (getAllowNull(props) !== true && props.value == null) {
        //LL: 15.10.21:
        if (props.allowZeroInsteadOfNull ?? options?.allowZeroInsteadOfNull) {
            zeroValue = 0;
        } else {
            content = (
                <input
                    title={props.tooltip}
                    type="text"
                    className={(props.isNotFormControl ? '' : 'form-control ') + 'disabled'}
                    value={'Not set'}
                    readOnly={true}
                    placeholder={props.placeholder}
                />
            );
        }
    }

    if (content == null) {
        const brokenObj = checkConstraints(value, props);

        let style: React.CSSProperties = {};

        if (brokenObj.broken) {
            style = { border: '1px solid red' };
        }

        content = (
            <input
                title={props.tooltip}
                type="text"
                className={props.isNotFormControl ? undefined : 'form-control'}
                ref={props.inputControlRef}
                value={zeroValue != null ? zeroValue : value}
                readOnly={readOnly}
                style={style}
                placeholder={props.placeholder}
                tabIndex={readOnly ? -1 : 0}
                //onFocus={handleOnFocus}
                onBlur={handleOnBlur}
                onInput={handleOnChange}
                onChange={() => {}} // Is added to fix red warning.
                onKeyUp={handleKeyUp}
            />
        );
    }

    return <div className={'number-field' + (options?.inline ? ' inline' : '')}>{content}</div>;
}

export default function NumberField(props: INumberFieldProperties) {
    const [value, setValue] = React.useState(getStringValue(props));

    const inputControlRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        if (props.setFocus || (props.options != null && props.options.setFocus)) {
            if (inputControlRef.current != null) {
                setTimeout(() => {
                    inputControlRef.current.focus();
                }, 20);
            }
        }
    }, []);

    React.useEffect(() => {
        const newValue = getStringValue(props);

        if (newValue !== value) {
            setValue(newValue);
        }
    }, [props.value]);

    const handleOnSetState = React.useCallback((newValue: string) => {
        setValue(newValue);
    }, []);

    const handleOnChange = React.useCallback(
        (newValue: number) => {
            if (props.onChange != null) {
                if (newValue !== getValue(props.value, isInteger(props))) {
                    props.onChange(newValue);
                }
            }
        },
        [props.onChange]
    );

    return (
        <NumberFieldStateless
            {...props}
            value={value}
            inputControlRef={inputControlRef}
            onSetState={handleOnSetState}
            onChange={handleOnChange}
        />
    );
}
